// src/components/notify/NotificationCenter.js
// FlowSense Notify — notification centre (drawer)
//   views: All · Unread · Needs ack · Critical · Snoozed · Archived  ·  category chips  ·  search
//   FlowSense AI digest · grouped by day · infinite "load more" · live updates · settings
import React, { useCallback, useMemo, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
    DrawerHeader, DrawerOverlay, Flex, HStack, Icon, IconButton, Input, InputGroup, InputLeftElement, Skeleton,
    Stack, Tab, TabList, Tabs, Text, Tooltip, useColorModeValue, useDisclosure, useToast, Wrap, WrapItem,
} from '@chakra-ui/react';
import { MdDoneAll, MdNotificationsNone, MdSearch, MdSettings } from 'react-icons/md';
import useNotifyInbox from '../../hooks/useNotifyInbox';
import * as api from '../../services/notifyApi';
import NotificationItem from './NotificationItem';
import NotifyAiDigest from './NotifyAiDigest';
import NotificationPreferences from './NotificationPreferences';
import { CATEGORY_META, groupByDay } from './notifyFormat';

const VIEWS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread', count: 'unread' },
    { key: 'ack', label: 'Needs ack', count: 'ack_pending', scheme: 'red' },
    { key: 'critical', label: 'Urgent', count: 'unread_critical', scheme: 'red' },
    { key: 'snoozed', label: 'Snoozed', count: 'snoozed' },
    { key: 'archived', label: 'Archived' },
];

const STATUS_DOT = {
    live: { color: 'green.400', label: 'Live' },
    connecting: { color: 'yellow.400', label: 'Connecting…' },
    reconnecting: { color: 'orange.400', label: 'Reconnecting…' },
    polling: { color: 'blue.400', label: 'Updating every 30 s' },
    offline: { color: 'red.400', label: 'Offline' },
    idle: { color: 'gray.400', label: 'Idle' },
};

const EMPTY_TEXT = {
    all: "You're all caught up.",
    unread: 'No unread notifications.',
    ack: 'Nothing waiting for your acknowledgement.',
    critical: 'No urgent notifications.',
    snoozed: 'Nothing snoozed.',
    archived: 'Archive is empty.',
};

export default function NotificationCenter({ isOpen, onClose, summary, status = 'idle', onNavigate }) {
    const toast = useToast();
    const headerBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const dayBg = useColorModeValue('gray.50', 'gray.900');
    const gradient = useColorModeValue('linear(to-r, purple.600, teal.500)', 'linear(to-r, purple.300, teal.300)');
    const prefs = useDisclosure();

    const [viewIndex, setViewIndex] = useState(0);
    const [category, setCategory] = useState('');
    const [q, setQ] = useState('');
    const view = VIEWS[viewIndex].key;
    const inbox = useNotifyInbox({ view, category, q, enabled: isOpen });
    const groups = useMemo(() => groupByDay(inbox.items), [inbox.items]);
    const [digestKey, setDigestKey] = useState(0);
    const bumpDigest = useCallback(() => setDigestKey(k => k + 1), []);

    const fail = useCallback((title) => (e) => toast({ status: 'error', title, description: e?.message, duration: 4000 }), [toast]);

    const open = useCallback((n) => {
        if (!n.is_read) inbox.markRead(n.id).catch(() => {});
        const url = n.action_url || (n.entity?.type === 'work_order' ? `/admin/workorders?workOrderId=${encodeURIComponent(n.entity.id)}` /* --- UPDATE: real route */ : null);
        if (url) { onNavigate?.(url); onClose?.(); }
    }, [inbox, onNavigate, onClose]);

    const handlers = useMemo(() => ({
        onRead: (n) => inbox.markRead(n.id).catch(fail('Could not mark as read')),
        onUnread: (n) => inbox.markUnread(n.id).catch(fail('Could not mark as unread')),
        onAck: (n) => inbox.acknowledge(n.id).then(() => { bumpDigest(); toast({ status: 'success', title: 'Acknowledged', duration: 2000 }); }).catch(fail('Could not acknowledge')),
        onArchive: (n) => inbox.archive(n.id).catch(fail('Could not archive')),
        onUnarchive: (n) => inbox.unarchive(n.id).catch(fail('Could not restore')),
        onSnooze: (n, m) => inbox.snooze(n.id, m).then(() => toast({ status: 'info', title: `Snoozed for ${m >= 60 ? `${Math.round(m / 60)} h` : `${m} min`}`, duration: 2000 })).catch(fail('Could not snooze')),
        onDelete: (n) => inbox.remove(n.id).catch(fail('Could not delete')),
    }), [inbox, fail, toast, bumpDigest]);

    const readAll = useCallback(() => {
        inbox.readAll(category || undefined).then(bumpDigest).catch(fail('Could not mark all as read'));
    }, [inbox, category, fail, bumpDigest]);

    const muteCategory = useCallback(async (cat) => {
        const doc = await api.getPreferences();
        await api.savePreferences({ categories: doc.categories.map(c => (c.key === cat ? { ...c, push: false } : c)) });
        toast({ status: 'success', title: `${CATEGORY_META[cat]?.label || cat}: push off`, description: 'They still arrive in your inbox.', duration: 3000 });
    }, [toast]);

    const dot = STATUS_DOT[status] || STATUS_DOT.idle;
    const categoriesWithUnread = useMemo(() => Object.entries(summary?.by_category || {}).filter(([, c]) => c > 0), [summary]);

    return (
        <>
            <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton mt={1} />
                    <DrawerHeader borderBottomWidth="1px" borderColor={border} bg={headerBg} pb={2}>
                        <Flex align="center" gap={2} pr={8}>
                            <Text bgGradient={gradient} bgClip="text" fontWeight="800" fontSize="lg">Notifications</Text>
                            <Tooltip label={dot.label} hasArrow>
                                <HStack spacing={1} aria-label={`Connection: ${dot.label}`}>
                                    <Box w="8px" h="8px" borderRadius="full" bg={dot.color} />
                                    <Text fontSize="xs" color={muted} fontWeight="500">{dot.label}</Text>
                                </HStack>
                            </Tooltip>
                            <Box flex="1" />
                            <Tooltip label={category ? 'Mark this topic read' : 'Mark all read'} hasArrow>
                                <IconButton size="sm" variant="ghost" icon={<MdDoneAll />} aria-label="Mark all read" onClick={readAll} isDisabled={!summary?.unread} />
                            </Tooltip>
                            <Tooltip label="Notification settings" hasArrow>
                                <IconButton size="sm" variant="ghost" icon={<MdSettings />} aria-label="Notification settings" onClick={prefs.onOpen} />
                            </Tooltip>
                        </Flex>
                        <Tabs index={viewIndex} onChange={setViewIndex} variant="soft-rounded" colorScheme="purple" size="sm" mt={2} isLazy>
                            <TabList overflowX="auto" pb={1} gap={1}>
                                {VIEWS.map(v => (
                                    <Tab key={v.key} whiteSpace="nowrap" px={3}>
                                        {v.label}
                                        {v.count && summary?.[v.count] > 0 && <Badge ml={1.5} colorScheme={v.scheme || 'purple'} borderRadius="full">{summary[v.count]}</Badge>}
                                    </Tab>
                                ))}
                            </TabList>
                        </Tabs>
                        <InputGroup size="sm" mt={2}>
                            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color={muted} /></InputLeftElement>
                            <Input placeholder="Search notifications" value={q} onChange={(e) => setQ(e.target.value)} borderRadius="lg" aria-label="Search notifications" />
                        </InputGroup>
                        {(categoriesWithUnread.length > 0 || category) && (
                            <Wrap spacing={1} mt={2}>
                                <WrapItem><Button size="xs" borderRadius="full" variant={category ? 'outline' : 'solid'} colorScheme="purple" onClick={() => setCategory('')}>All topics</Button></WrapItem>
                                {[...new Set([...(category ? [category] : []), ...categoriesWithUnread.map(([k]) => k)])].map(k => {
                                    const meta = CATEGORY_META[k] || CATEGORY_META.general;
                                    return (
                                        <WrapItem key={k}>
                                            <Button size="xs" borderRadius="full" leftIcon={<Icon as={meta.icon} />} variant={category === k ? 'solid' : 'outline'} colorScheme={meta.color} onClick={() => setCategory(c => (c === k ? '' : k))}>
                                                {meta.label}{summary?.by_category?.[k] ? ` · ${summary.by_category[k]}` : ''}
                                            </Button>
                                        </WrapItem>
                                    );
                                })}
                            </Wrap>
                        )}
                    </DrawerHeader>
                    <DrawerBody p={0}>
                        {view === 'all' && !q && !category && (
                            <NotifyAiDigest refreshKey={(summary?.unread || 0) + digestKey} onMuteCategory={muteCategory} onFocus={open} />
                        )}
                        {inbox.error && !inbox.items.length ? (
                            <Alert status="error" m={3} borderRadius="lg" w="auto">
                                <AlertIcon /><AlertDescription flex="1" fontSize="sm">{inbox.error.message}</AlertDescription>
                                <Button size="sm" onClick={inbox.reload}>Retry</Button>
                            </Alert>
                        ) : inbox.loading && !inbox.items.length ? (
                            <Stack p={3} spacing={3}>{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} h="64px" borderRadius="lg" />)}</Stack>
                        ) : !inbox.items.length ? (
                            <Flex direction="column" align="center" justify="center" py={16} px={6} color={muted} textAlign="center">
                                <Icon as={MdNotificationsNone} boxSize={12} mb={2} />
                                <Text fontWeight="600">{q ? `No results for “${q}”` : EMPTY_TEXT[view]}</Text>
                            </Flex>
                        ) : (
                            <Box mt={2}>
                                {groups.map(g => (
                                    <Box key={g.label}>
                                        <Text position="sticky" top={0} zIndex={1} bg={dayBg} px={3} py={1} fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" letterSpacing="wide">{g.label}</Text>
                                        {g.items.map(n => <NotificationItem key={n.id} n={n} onOpen={open} {...handlers} />)}
                                    </Box>
                                ))}
                                {inbox.hasMore && (
                                    <Flex justify="center" py={3}><Button size="sm" variant="ghost" onClick={inbox.loadMore} isLoading={inbox.loadingMore}>Load more</Button></Flex>
                                )}
                            </Box>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
            {prefs.isOpen && <NotificationPreferences isOpen={prefs.isOpen} onClose={prefs.onClose} />}
        </>
    );
}
